import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Button from "@/js/common/ui/Button";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import Util from "@/js/common/Util";

interface Props {
  ognzCd: string; // NEW: "" / EDIT: "..."
  sysAuthrtSeCd: string; // NEW: "" / EDIT: "..."
  setModalOpen: Function;
  setCallTime: Function;
  listData: any[]; // res.result.list 그대로 넘기면 됨
}

/**
 * SysOgnzAuthrtSaveDialog.tsx
 * ------------------------------------------------------------
 * - 상세조회
 * - NEW 등록 시: (ognzCd + sysAuthrtSeCd) 중복이면 저장 막기
 */

const SysOgnzAuthrtSaveDialog = ({
  ognzCd,
  sysAuthrtSeCd,
  setModalOpen,
  setCallTime,
  listData,
}: Props) => {
  const isEdit = !Util.isEmpty(ognzCd) && !Util.isEmpty(sysAuthrtSeCd);

  const [upOgnzCdVal, setUpOgnzCdVal] = React.useState<string>("");
  const [ognzCdVal, setOgnzCdVal] = React.useState<string>("");
  const [sysAuthrtSeCdVal, setSysAuthrtSeCdVal] = React.useState<string>("");
  const [sysAuthrtYn, setSysAuthrtYn] = React.useState<string>("N");
  const [sysEvdncUseYn, setSysEvdncUseYn] = React.useState<string>("N");
  const [bgngYmd, setBgngYmd] = React.useState<string>("");
  const [endYmd, setEndYmd] = React.useState<string>("");

  // 상세조회(EDIT일 때만)
  React.useEffect(() => {
    if (!isEdit) {
      setUpOgnzCdVal("");
      setOgnzCdVal("");
      setSysAuthrtSeCdVal("");
      setSysAuthrtYn("Y");
      setSysEvdncUseYn("Y");
      setBgngYmd("");
      setEndYmd("99991231");
      return;
    }

    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/sys/ognz/authrt/select-ognz-authrt-dtl.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        ognzCd: ognzCd,
        sysAuthrtSeCd: sysAuthrtSeCd,
      },
      onSuccessFn: (res: any) => {
        const item = res.result.item;

        setUpOgnzCdVal(item.upOgnzCd);
        setOgnzCdVal(item.ognzCd);
        setSysAuthrtSeCdVal(item.sysAuthrtSeCd);
        setSysAuthrtYn(item.sysAuthrtYn);
        setSysEvdncUseYn(item.sysEvdncUseYn);
        setBgngYmd(item.bgngYmd);
        setEndYmd(item.endYmd);

        AppContext.hideSpinner();
      },
    });
  }, [ognzCd, sysAuthrtSeCd, isEdit]);

  const closeModal = () => {
    setModalOpen(false);
    setCallTime(new Date());
  };

  const validate = () => {
    if (Util.isEmpty(upOgnzCdVal)) {
      AppContext.openAlert({ title: "확인", msg: "본부를 선택해주세요.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(ognzCdVal)) {
      AppContext.openAlert({ title: "확인", msg: "지부를 선택해주세요.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(sysAuthrtSeCdVal)) {
      AppContext.openAlert({ title: "확인", msg: "시스템 권한 구분을 선택해주세요.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(bgngYmd)) {
      AppContext.openAlert({ title: "확인", msg: "시작일을 입력해주세요.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(endYmd)) {
      AppContext.openAlert({ title: "확인", msg: "종료일을 입력해주세요.", icon: "warning" });
      return false;
    }
    if (Number(bgngYmd) > Number(endYmd)) {
      AppContext.openAlert({
        title: "기간 확인",
        msg: "작성 종료일은 시작일 이후로 설정해 주세요.",
        icon: "warning",
      });
      return false;
    }

    if (!isEdit) {
      const exists = (Array.isArray(listData) ? listData : []).some(
        (r: any) =>
          String(r.ognzCd) === String(ognzCdVal) &&
          String(r.sysAuthrtSeCd) === String(sysAuthrtSeCdVal) &&
          String(r.delYn || "N") !== "Y"
      );

      if (exists) {
        AppContext.openAlert({
          title: "확인",
          msg: "이미 등록된 조직 권한입니다.",
          icon: "warning",
        });
        return false;
      }
    }

    return true;
  };

  const onSave = () => {
    if (!validate()) return;

    AppContext.openAlert({
      title: isEdit ? "수정" : "등록",
      msg: isEdit ? "수정하시겠습니까?" : "등록하시겠습니까?",
      icon: "warning",
      confirmText: isEdit ? "수정" : "등록",
      handleConfirm: () => {
        AppContext.showSpinner();
        Axios.dataAccess({
          url: "system/sys/ognz/authrt/save-ognz-authrt.do",
          methodType: "post",
          paramType: "json",
          paramData: {
            ognzCd: ognzCdVal,
            sysAuthrtSeCd: sysAuthrtSeCdVal,
            sysAuthrtYn: sysAuthrtYn,
            sysEvdncUseYn: sysEvdncUseYn,
            bgngYmd: bgngYmd,
            endYmd: endYmd,
          },
          onSuccessFn: () => {
            AppContext.hideSpinner();
            AppContext.openAlert({
              title: "완료",
              msg: isEdit ? "수정되었습니다." : "등록되었습니다.",
              icon: "success",
            });
            setCallTime(new Date());
            setModalOpen(false);
          },
        });
      },
    });
  };

  const onDelete = () => {
    AppContext.openAlert({
      title: "삭제",
      msg: "삭제하시겠습니까?",
      icon: "warning",
      confirmText: "삭제",
      handleConfirm: () => {
        AppContext.showSpinner();
        Axios.dataAccess({
          url: "system/sys/ognz/authrt/delete-ognz-authrt.do",
          methodType: "post",
          paramType: "json",
          paramData: {
            ognzCd: ognzCdVal,
            sysAuthrtSeCd: sysAuthrtSeCdVal,
          },
          onSuccessFn: () => {
            AppContext.hideSpinner();
            AppContext.openAlert({ title: "완료", msg: "삭제되었습니다.", icon: "success" });
            setCallTime(new Date());
            setModalOpen(false);
          },
        });
      },
    });
  };

  return (
    <div className="w100p dpFlx jcC">
      <div className="w400">
        {/* 본부 */}
        <div className="mt10 ml10">
          <div className="h30 dpFlx aiE pb5 bdBox">
            <span className="fm-b fs13 ls06 fc111111">
              본부 <span className="fcE60000">*</span>
            </span>
          </div>
          <SelectBox
            value={upOgnzCdVal}
            width={380}
            height={36}
            fontSize={14}
            fontFamily="fm-c"
            color="#333"
            letterSpacing={-1}
            borderColor="#ddd"
            borderOnColor="#222"
            borderRadius={5}
            background="#fff"
            codeArray={Util.getUpOrgCodeArray("01")}
            placeholder="선택"
            paddingRight={8}
            onChangeFunc={(v: string) => {
              setUpOgnzCdVal(v);
              setOgnzCdVal("");
            }}
            disabled={isEdit}
          />
        </div>

        {/* 지부 */}
        <div className="mt10 ml10">
          <div className="h30 dpFlx aiE pb5 bdBox">
            <span className="fm-b fs13 ls06 fc111111">
              지부 <span className="fcE60000">*</span>
            </span>
          </div>
          <SelectBox
            value={ognzCdVal}
            width={380}
            height={36}
            fontSize={14}
            fontFamily="fm-c"
            color="#333"
            letterSpacing={-1}
            borderColor="#ddd"
            borderOnColor="#222"
            borderRadius={5}
            background="#fff"
            codeArray={Util.getDownOrgCodeArray(upOgnzCdVal)}
            placeholder="선택"
            paddingRight={8}
            onChangeFunc={(v: string) => setOgnzCdVal(v)}
            disabled={isEdit}
          />
        </div>

        {/* 시스템 권한 구분 */}
        <div className="mt10 ml10">
          <div className="h30 dpFlx aiE pb5 bdBox">
            <span className="fm-b fs13 ls06 fc111111">
              시스템 권한 구분 <span className="fcE60000">*</span>
            </span>
          </div>
          <SelectBox
            value={sysAuthrtSeCdVal}
            width={380}
            height={36}
            fontSize={14}
            fontFamily="fm-c"
            color="#333"
            letterSpacing={-1}
            borderColor="#ddd"
            borderOnColor="#222"
            borderRadius={5}
            background="#fff"
            codeArray={Util.getCodeArray("SYS_AUTHRT_SE_CD")}
            placeholder="선택"
            paddingRight={8}
            onChangeFunc={(v: string) => setSysAuthrtSeCdVal(v)}
            disabled={isEdit}
          />
        </div>

        {/* 권한 여부 */}
        <div className="mt10 ml10">
          <div className="h30 dpFlx aiE pb5 bdBox">
            <span className="fm-b fs13 ls06 fc111111">권한 여부</span>
          </div>
          <SelectBox
            value={sysAuthrtYn}
            width={380}
            height={36}
            fontSize={14}
            fontFamily="fm-c"
            color="#333"
            letterSpacing={-1}
            borderColor="#ddd"
            borderOnColor="#222"
            borderRadius={5}
            background="#fff"
            codeArray={[
              { code: "Y", name: "사용" },
              { code: "N", name: "미사용" },
            ]}
            placeholder=""
            paddingRight={8}
            onChangeFunc={(v: string) => setSysAuthrtYn(v)}
          />
        </div>

        {/* 권한 여부 */}
        <div className="mt10 ml10">
          <div className="h30 dpFlx aiE pb5 bdBox">
            <span className="fm-b fs13 ls06 fc111111">증빙서류 사용여부</span>
          </div>
          <SelectBox
            value={sysEvdncUseYn}
            width={380}
            height={36}
            fontSize={14}
            fontFamily="fm-c"
            color="#333"
            letterSpacing={-1}
            borderColor="#ddd"
            borderOnColor="#222"
            borderRadius={5}
            background="#fff"
            codeArray={[
              { code: "Y", name: "사용" },
              { code: "N", name: "미사용" },
            ]}
            placeholder=""
            paddingRight={8}
            onChangeFunc={(v: string) => setSysEvdncUseYn(v)}
          />
        </div>

        {/* 시작/종료일 */}
        <div className="dpFlx flxNowrap mt10 jcC ml10">
          <div className="w260 ">
            <div className="h30 dpFlx aiE pb5 bdBox">
              <span className="fm-b fs13 ls06 fc111111">
                시작일 <span className="fcE60000">*</span>
              </span>
            </div>
            <DatePickerBox
              width={185}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={5}
              bgColor="white"
              textAlign="left"
              calendarThemeColor={AppContext.themeColor}
              className="mr5"
              value={bgngYmd}
              onChange={(v: string) => setBgngYmd(v)}
            />
          </div>

          <div className="w260">
            <div className="h30 dpFlx aiE pb5 bdBox">
              <span className="fm-b fs13 ls06 fc111111">
                종료일 <span className="fcE60000">*</span>
              </span>
            </div>
            <DatePickerBox
              width={185}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={5}
              bgColor="white"
              textAlign="left"
              calendarThemeColor={AppContext.themeColor}
              className="mr5"
              value={endYmd}
              onChange={(v: string) => setEndYmd(v)}
            />
          </div>
        </div>

        {/* 버튼 */}
        <div className="dpFlx jcC mt25 gap10 pb10">
          <Button
            width={100}
            height={40}
            fontFamily="fm-b"
            fontSize={16}
            letterSpacing={-1}
            variant="styCancelBtn"
            borderRadius={6}
            buttonClass="dpFlx aiC jcC"
            buttonName="취소"
            onClickFunc={closeModal}
          />

          {isEdit && (
            <Button
              width={100}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              variant="styDeleteBtn"
              borderRadius={6}
              buttonClass="dpFlx aiC jcC"
              buttonName="삭제"
              onClickFunc={onDelete}
            />
          )}

          <Button
            width={100}
            height={40}
            fontFamily="fm-b"
            fontSize={16}
            letterSpacing={-1}
            variant="styTempBtn"
            borderRadius={6}
            buttonClass="dpFlx aiC jcC"
            buttonName={isEdit ? "수정" : "등록"}
            onClickFunc={onSave}
          />
        </div>
      </div>
    </div>
  );
};

export default SysOgnzAuthrtSaveDialog;
